/**
 * Public voucher check. Every voucher's QR code encodes
 * `https://www.pawsitivefuture.org/voucher?code=<code>`; the page at that address asks the backend
 * whether the voucher can be used (GET /api/public/vouchers/:code/availability — no login).
 *
 * The backend answers availability and nothing else: no amount, organization, beneficiary or
 * batch. Anything this client cannot read as one of the five answers below is treated as "could
 * not check", never guessed at.
 */
import { apiBase } from '../lib/api';

export type VoucherAvailabilityReason = 'AVAILABLE' | 'EXPIRED' | 'USED' | 'VOID' | 'NOT_RECOGNISED';

export interface VoucherAvailability {
  readonly available: boolean;
  readonly reason: VoucherAvailabilityReason;
  /** ISO-8601 instant the voucher stops being accepted. Present only when available. */
  readonly expiresAt?: string;
}

const REASONS: readonly string[] = ['AVAILABLE', 'EXPIRED', 'USED', 'VOID', 'NOT_RECOGNISED'];

/** Whether `body` is an answer this page knows how to show. */
function isVoucherAvailability(body: unknown): body is VoucherAvailability {
  if (typeof body !== 'object' || body === null) return false;
  const { available, reason, expiresAt } = body as Record<string, unknown>;
  if (typeof reason !== 'string' || !REASONS.includes(reason)) return false;
  if (available !== (reason === 'AVAILABLE')) return false;
  return !available || (typeof expiresAt === 'string' && !Number.isNaN(Date.parse(expiresAt)));
}

/**
 * Ask the backend whether the voucher with `code` can be used.
 * @throws when the check could not be made — offline, a non-2xx (the API answers 503 when it
 *   cannot look the voucher up), or an answer outside the contract. The caller offers a retry.
 */
export async function fetchVoucherAvailability(code: string, signal?: AbortSignal): Promise<VoucherAvailability> {
  const res = await fetch(`${apiBase}/api/public/vouchers/${encodeURIComponent(code)}/availability`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Voucher check failed: HTTP ${res.status}`);
  const body: unknown = await res.json();
  if (!isVoucherAvailability(body)) throw new Error('Voucher check returned an unexpected answer');
  return body;
}
